import React, { useEffect, useState, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { ApiValidationFailedException } from '../store/modules/exceptions';

const useStyles = createUseStyles((theme: Theme) => ({
  saveStatus: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    display: 'inline-flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.blue,
    marginRight: theme.marginBase,
    marginLeft: theme.marginBase,
  },

  iconError: {
    color: theme.colors.redAccessible,
  },
  rtl: {
    flexDirection: 'row-reverse',
  },
  error: {
    color: theme.colors.redAccessible,
  },
}));

interface Props {
  loading: boolean;
  error?: boolean | Error | null;
  errorMessage?: string;
  dirty?: boolean;
  rtl?: boolean;
  name?: string;
}

export const SaveStatus = ({
  loading,
  rtl,
  dirty,
  error,
  errorMessage,
  name,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const [hidden, setHidden] = useState(true);

  const timeoutLoadingRef = useRef<NodeJS.Timeout | null>(null);

  const message = errorMessage
    ? errorMessage
    : error instanceof ApiValidationFailedException && name
    ? (error.getFormikErrors()[name] as string)
    : undefined;

  useEffect(() => {
    if (dirty && !loading) {
      setHidden(true);
    }

    if (loading || (error && !dirty)) {
      timeoutLoadingRef.current = setTimeout(() => {
        setHidden(false);
      }, 300);
    }
  }, [dirty, loading, error]);

  if (hidden) {
    return null;
  }

  if (loading) {
    return (
      <div className={classnames(classes.saveStatus, { [classes.rtl]: rtl })}>
        <span className={classes.icon}>
          <CircularProgress size={15} />
        </span>
        {t('saveStatus.loading')}
      </div>
    );
  }

  if (error || message) {
    return (
      <div
        className={classnames(classes.saveStatus, classes.error, {
          [classes.rtl]: rtl,
        })}
      >
        <i
          className={classnames(
            classes.icon,
            classes.iconError,
            'ri-error-warning-line',
          )}
        />
        {message ? message : t('saveStatus.failed')}
      </div>
    );
  }

  return (
    <div className={classnames(classes.saveStatus, { [classes.rtl]: rtl })}>
      <i className={classnames(classes.icon, 'ri-check-line')} />
      {t('saveStatus.success')}
    </div>
  );
};
